import { CalculatedOdds, Odds, OddsOperations, emptyCalculatedOdds, emptyOdds } from '@frontend/sports/odds/feature';
import { Decimal } from 'decimal.js';

import { IBetslip, ISlip } from '../../bet-generation/models';
import { ITaxationResult } from '../../taxation/models';
import { IBetslipSummary } from '../models';

export function getSlipTotalStakeAndBetCount(slip: ISlip): { stake: Decimal | null; totalStake: Decimal | null; betCount: number } {
    const betCount = slip.bets.filter((b) => b.isValid).reduce((count, bet) => count + bet.betCount, 0);
    const stake = slip.stake === null ? null : new Decimal(slip.stake);
    const totalStake = stake === null ? null : stake.times(betCount);

    return { totalStake, stake, betCount };
}

export class BetslipSummaryCalculations {
    static combineSummaryViewModels(summaries: [IBetslip, IBetslipSummary][]): [IBetslip, IBetslipSummary] {
        return summaries.reduce(
            (aggr, summ) => {
                const betslipResult = aggr[0];
                const summaryResult = aggr[1];
                const betslip = summ[0];
                const summary = summ[1];

                aggr[0].slips = [...(betslipResult.slips || []), ...(betslip.slips || [])];

                aggr[1] = {
                    slipSummaries: [...(summaryResult.slipSummaries || []), ...(summary.slipSummaries || [])],
                    possibleWinnings: this.sumNullableDecimals(summaryResult.possibleWinnings, summary.possibleWinnings),
                    possibleWinningsNet: this.sumNullableDecimals(summaryResult.possibleWinningsNet, summary.possibleWinningsNet),
                    totalStake: this.sumNullableDecimals(summaryResult.totalStake, summary.totalStake),
                    taxation: this.sumTaxationResults(summaryResult.taxation, summary.taxation),
                    betCount: new Decimal(summaryResult.betCount || 0).add(new Decimal(summary.betCount || 0)).toNumber(),
                    totalOdds: this.sumCalculatedOdds(summaryResult.totalOdds, summary.totalOdds),
                    teaserTotalOdds: this.sumOdds(summaryResult.teaserTotalOdds, summary.teaserTotalOdds),
                    averageOdds: this.sumCalculatedOdds(summaryResult.averageOdds, summary.averageOdds),
                    isPotentailReturn: summaryResult.isPotentailReturn || summary.isPotentailReturn,
                    hasBetBuilder: summaryResult.hasBetBuilder || summary.hasBetBuilder,
                    missingBetBuilderLegs: summaryResult.missingBetBuilderLegs || summary.missingBetBuilderLegs,
                };

                return aggr;
            },
            [{} as IBetslip, {} as IBetslipSummary],
        );
    }

    static sumNullableDecimals(a: Decimal | null | undefined, b: Decimal | null | undefined): Decimal | null {
        if ((!a || a.isZero()) && (!b || b.isZero())) {
            return null;
        }

        return (a || new Decimal(0)).add(b || 0);
    }

    static sumTaxationResults(a: ITaxationResult | null, b: ITaxationResult | null): ITaxationResult | null {
        if (!a && !b) {
            return null;
        }

        return {
            amount: this.sumNullableDecimals(a?.amount, b?.amount)!,
            rate: this.sumNullableDecimals(a?.rate, b?.rate)!,
            liabilityAmount: this.sumNullableDecimals(a?.liabilityAmount, b?.liabilityAmount)!,
            isPlaceholderTax: a?.isPlaceholderTax || b?.isPlaceholderTax,
        };
    }

    static sumOdds(a: Odds = emptyOdds, b: Odds = emptyOdds): Odds {
        return {
            decimals: new Decimal(a.decimals).add(new Decimal(b.decimals)).toNumber(),
            fractional: {
                numerator: new Decimal(a.fractional.numerator).add(new Decimal(b.fractional.numerator)).toNumber(),
                denominator: new Decimal(a.fractional.denominator).add(new Decimal(b.fractional.denominator)).toNumber(),
            },
            moneyline: new Decimal(a.moneyline).add(new Decimal(b.moneyline)).toNumber(),
        };
    }

    static sumCalculatedOdds(a: CalculatedOdds = emptyCalculatedOdds, b: CalculatedOdds = emptyCalculatedOdds): CalculatedOdds {
        return OddsOperations.sum([a, b]);
    }
}
